/**
 * Maps ApiPricingBreakdown or PricingBreakdown to MinimumPricingBreakdown
 * @param pricingBreakdown The input pricing breakdown object
 * @returns MinimumPricingBreakdown with simplified structure
 */

import {
  ApiPricingBreakdown,
  MinimumDataPrice,
  MinimumPricingBreakdown,
  PricingBreakdown,
} from '@interfaces/pricingBreakdown';
import { Price } from '@interfaces/models/price';
import { BuyingOfferProduct } from '@interfaces/models/negotiation/domain/BuyingOffer';

export function mapToMinimumPricingBreakdown(
  pricingBreakdown: ApiPricingBreakdown | PricingBreakdown,
  productPriceFallback?: Price,
): MinimumPricingBreakdown {
  if (!pricingBreakdown) {
    return null;
  }

  // Extract price data
  const extractMinimumDataPrice = (price?: Price): MinimumDataPrice => {
    if (!price) {
      return { formatted: '', cents: 0 };
    }
    return {
      formatted: price.formatted,
      cents: price.cents,
    };
  };

  // Calculate buyer fees amount
  const buyerFeesAmount: MinimumDataPrice = {
    formatted: pricingBreakdown.buyerFees?.amount.formatted,
    cents: pricingBreakdown.buyerFees?.amount.cents,
  };

  const formattedPricingBreakdown: MinimumPricingBreakdown = {
    // Basic price data
    sellerPrice: extractMinimumDataPrice(pricingBreakdown.sellerPrice),
    sellerEarning:
      'sellerEarning' in pricingBreakdown && pricingBreakdown.sellerEarning
        ? extractMinimumDataPrice(pricingBreakdown.sellerEarning)
        : null,
    sellerInitialPrice:
      'sellerInitialPrice' in pricingBreakdown && pricingBreakdown.sellerInitialPrice
        ? extractMinimumDataPrice(pricingBreakdown.sellerInitialPrice)
        : null,
    fullPrice:
      'fullPrice' in pricingBreakdown && pricingBreakdown.fullPrice
        ? extractMinimumDataPrice(pricingBreakdown.fullPrice)
        : extractMinimumDataPrice(productPriceFallback),

    // Fees data
    buyerFees: buyerFeesAmount,
    hasCrossBorderFees: pricingBreakdown.buyerFees?.hasCrossBorderFees,
    includesAuthenticationFees: pricingBreakdown.buyerFees?.includesAuthenticationFees,
  };

  return formattedPricingBreakdown;
}

// buyerTotal is a poor name choice but it's the correct one.
export function mapNegotiationProductToMinimumPricingBreakdown(product: BuyingOfferProduct): MinimumPricingBreakdown {
  const formattedPricingBreakdown: MinimumPricingBreakdown = {
    sellerPrice: product.priceDetails.buyerTotal,
    buyerFees: product.priceDetails.buyerFees,
    fullPrice: product.priceDetails.itemPrice,
    hasCrossBorderFees: product.buyerFeesIncludes.crossBorderFees,
    includesAuthenticationFees: product.buyerFeesIncludes.mandatoryAuthentication,
  };
  return formattedPricingBreakdown;
}
